<template>
  <label class="hs-search-select">
    <span v-if="label" class="hs-search-select__label">{{ label }}</span>
    <div class="hs-search-select__control">
      <input
        ref="input"
        :aria-expanded="isOpen"
        :aria-label="label || placeholder"
        :disabled="disabled"
        :placeholder="placeholder"
        :value="query"
        autocomplete="off"
        class="hs-search-select__input"
        role="combobox"
        @blur="handleBlur"
        @focus="focusInput"
        @input="handleInput"
        @keydown="handleKeydown"
      />
      <button
        v-if="clearable && normalizedValue && !disabled"
        aria-label="Clear selection"
        class="hs-search-select__clear"
        type="button"
        @click="clearSelection"
        @mousedown.prevent
      >
        ×
      </button>
      <span class="hs-search-select__chevron">⌄</span>
    </div>
    <span v-if="hint" class="hs-search-select__hint">{{ hint }}</span>

    <div v-if="isOpen && !disabled" class="hs-search-select__menu" role="listbox" @mousedown.prevent>
      <button
        v-for="(option, index) in filteredOptions"
        :key="option.value"
        :aria-selected="isSelected(option.value)"
        :class="['hs-search-select__option', { 'is-highlighted': index === highlightedIndex, 'is-selected': isSelected(option.value) }]"
        :disabled="option.disabled"
        role="option"
        type="button"
        @click="selectOption(option)"
        @mouseenter="highlightedIndex = index"
      >
        <span class="hs-search-select__option-label">{{ option.label }}</span>
        <span v-if="option.description" class="hs-search-select__option-description">{{ option.description }}</span>
      </button>
      <div v-if="!filteredOptions.length" class="hs-search-select__empty">{{ emptyText }}</div>
    </div>
  </label>
</template>

<script>
export default {
  name: "HSSearchSuggestionSelect",
  props: {
    modelValue: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "Search..." },
    hint: { type: String, default: "" },
    emptyText: { type: String, default: "No matches" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    maxVisible: { type: Number, default: 10 },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      isOpen: false,
      query: "",
      highlightedIndex: 0,
    };
  },
  computed: {
    normalizedValue() {
      return String(this.modelValue || "").trim();
    },
    normalizedOptions() {
      return this.options
        .filter((option) => option !== null && option !== undefined)
        .map((option) => ({
          value: String(option.value ?? ""),
          label: String(option.label ?? option.name ?? option.value ?? ""),
          description: option.description ? String(option.description) : "",
          keywords: Array.isArray(option.keywords) ? option.keywords.map(String) : [],
          disabled: Boolean(option.disabled),
        }));
    },
    selectedOption() {
      return this.normalizedOptions.find((option) => this.isSelected(option.value)) || null;
    },
    filteredOptions() {
      const needle = this.query.trim().toLowerCase();
      const source = needle
        ? this.normalizedOptions.filter((option) => this.optionSearchText(option).includes(needle))
        : this.normalizedOptions;
      return source.slice(0, this.maxVisible);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.syncQueryFromValue();
      },
    },
    filteredOptions() {
      this.highlightedIndex = Math.min(this.highlightedIndex, Math.max(0, this.filteredOptions.length - 1));
    },
  },
  methods: {
    optionSearchText(option) {
      return [option.value, option.label, option.description, ...option.keywords]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    },
    syncQueryFromValue() {
      this.query = this.selectedOption?.label || this.normalizedValue;
    },
    focusInput() {
      if (this.disabled) return;
      this.isOpen = true;
      this.query = "";
      this.highlightedIndex = 0;
      this.$nextTick(() => this.$refs.input?.select());
    },
    handleBlur() {
      window.setTimeout(() => this.closeAndReset(), 140);
    },
    closeAndReset() {
      this.isOpen = false;
      this.highlightedIndex = 0;
      this.syncQueryFromValue();
    },
    handleInput(event) {
      this.query = event.target.value;
      this.isOpen = true;
      this.highlightedIndex = 0;
    },
    handleKeydown(event) {
      if (this.disabled) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.isOpen = true;
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, Math.max(0, this.filteredOptions.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        this.highlightedIndex = Math.max(0, this.highlightedIndex - 1);
      } else if (event.key === "Enter") {
        if (!this.isOpen) return;
        event.preventDefault();
        const option = this.filteredOptions[this.highlightedIndex];
        if (option) this.selectOption(option);
      } else if (event.key === "Escape") {
        event.preventDefault();
        this.closeAndReset();
        this.$refs.input?.blur();
      }
    },
    selectOption(option) {
      if (option.disabled) return;
      this.$emit("update:modelValue", option.value);
      this.$emit("change", option.value);
      this.isOpen = false;
      this.highlightedIndex = 0;
      this.query = option.label;
    },
    clearSelection() {
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
      this.query = "";
      this.isOpen = true;
      this.$nextTick(() => this.$refs.input?.focus());
    },
    isSelected(value) {
      return String(value) === this.normalizedValue;
    },
  },
};
</script>

<style scoped>
.hs-search-select {
  display: block;
  min-width: 0;
  position: relative;
}

.hs-search-select__label {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
}

.hs-search-select__control {
  position: relative;
}

.hs-search-select__input {
  background: var(--hs-surface, #fff);
  border: 1px solid var(--hs-border, #dbe1ea);
  border-radius: 10px;
  color: var(--hs-text, #0f172a);
  font: inherit;
  min-height: 2.55rem;
  outline: none;
  padding: 0.55rem 2.4rem 0.55rem 0.7rem;
  width: 100%;
}

.hs-search-select__input:focus {
  border-color: var(--hs-primary, #3293ec);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 16%, transparent);
}

.hs-search-select__clear,
.hs-search-select__chevron {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.hs-search-select__clear {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
  font-weight: 900;
  right: 1.8rem;
}

.hs-search-select__chevron {
  color: var(--hs-muted, #64748b);
  pointer-events: none;
  right: 0.75rem;
}

.hs-search-select__hint {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.75rem;
  margin-top: 0.35rem;
}

.hs-search-select__menu {
  background: var(--hs-surface, #fff);
  border: 1px solid var(--hs-border, #dbe1ea);
  border-radius: 14px;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
  display: grid;
  gap: 0.25rem;
  left: 0;
  max-height: 18rem;
  overflow-y: auto;
  padding: 0.35rem;
  position: absolute;
  right: 0;
  top: calc(100% + 0.35rem);
  z-index: 80;
}

.hs-search-select__option {
  border-radius: 10px;
  padding: 0.55rem 0.65rem;
  text-align: left;
  transition: background 0.15s, color 0.15s;
}

.hs-search-select__option:hover,
.hs-search-select__option.is-highlighted {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
}

.hs-search-select__option.is-selected {
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--hs-primary, #3293ec) 35%, transparent);
}

.hs-search-select__option-label {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.86rem;
  font-weight: 850;
}

.hs-search-select__option-description {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.75rem;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hs-search-select__empty {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
  padding: 1rem;
  text-align: center;
}
</style>
